import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  format, addMonths, subMonths, startOfMonth, endOfMonth,
  startOfWeek, endOfWeek, addDays, isSameMonth, isToday,
} from 'date-fns';
import { Search, ChevronLeft, ChevronRight, ArrowLeft, Calendar, User } from 'lucide-react';

// ── Read-only admin viewer for staff availability.
//    Backend: GET /api/availability/staff/{user_id}
//    Permission: availability.view (admin & super_admin already have).

const DAYS_HEADER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const EXC_META = {
  UNAVAILABLE:        { label: 'Unavailable',  cls: 'bg-red-100 text-red-800 border-red-300' },
  LEAVE_SICK:         { label: 'Sick Leave',   cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  LEAVE_ANNUAL:       { label: 'Annual Leave', cls: 'bg-violet-100 text-violet-800 border-violet-300' },
  PREFERRED:          { label: 'Preferred',    cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  AVAILABLE_OVERRIDE: { label: 'Available',    cls: 'bg-blue-100 text-blue-800 border-blue-300' },
};

const SHIFT_PRESET_LABELS = {
  MORNING:   'Morning',
  AFTERNOON: 'Afternoon',
  EVENING:   'Evening',
  NIGHT:     'Active Night',
  SLEEPOVER: 'Sleepover',
  CUSTOM:    'Custom',
};

function rangeLabel(r) {
  const types = (r?.types || []).filter(t => t !== 'CUSTOM');
  if (types.length >= 1 && types.length <= 2) {
    return types.map(t => SHIFT_PRESET_LABELS[t] || t).join(' + ');
  }
  return `${r?.start_time || ''}–${r?.end_time || ''}`;
}

function excMeta(t) {
  return EXC_META[t] || { label: t || 'Other', cls: 'bg-slate-100 text-slate-700 border-slate-300' };
}


function MonthGrid({ date, weekly, exceptions }) {
  const grid = useMemo(() => {
    const ms = startOfMonth(date);
    const me = endOfMonth(date);
    const gs = startOfWeek(ms, { weekStartsOn: 1 });
    const ge = endOfWeek(me, { weekStartsOn: 1 });
    const days = [];
    let d = gs;
    while (d <= ge) { days.push(d); d = addDays(d, 1); }
    return days;
  }, [date]);

  const excMap = useMemo(() => {
    const m = {};
    (exceptions || []).forEach(e => { if (e.date) m[e.date] = e; });
    return m;
  }, [exceptions]);

  const weeklyByDow = useMemo(() => {
    const m = {};
    (weekly || []).forEach(w => { m[w.day_of_week] = w; });
    return m;
  }, [weekly]);

  return (
    <div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAYS_HEADER.map(d => (
          <div key={d} className="text-[10px] uppercase tracking-wider text-muted-foreground text-center font-semibold">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {grid.map(d => {
          const dk = format(d, 'yyyy-MM-dd');
          const exc = excMap[dk];
          const dow = d.getDay();
          const wk = weeklyByDow[dow];
          const inMonth = isSameMonth(d, date);
          const today = isToday(d);

          let cellCls = 'bg-white border-slate-200';
          let badge = null;

          if (exc) {
            const m = excMeta(exc.type);
            cellCls = m.cls + ' border';
            badge = <div className="text-[9px] font-medium truncate">{m.label}</div>;
          } else if (wk && (wk.ranges || []).length > 0) {
            cellCls = 'bg-emerald-50 border-emerald-200';
            const r = wk.ranges[0];
            badge = <div className="text-[9px] tabular-nums text-emerald-800 truncate" title={`${r.start_time}–${r.end_time}`}>{rangeLabel(r)}</div>;
          } else {
            cellCls = 'bg-slate-50 border-slate-200';
          }

          const dimCls = inMonth ? '' : 'opacity-40';
          const todayCls = today ? 'ring-2 ring-blue-500' : '';

          return (
            <div key={dk} className={'rounded-md border p-1 min-h-[58px] ' + cellCls + ' ' + dimCls + ' ' + todayCls}>
              <div className="text-[11px] font-semibold tabular-nums leading-none">{format(d, 'd')}</div>
              <div className="mt-1">{badge}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function StaffAvailabilityDetail({ staff, onBack }) {
  const [data, setData] = useState({ weekly: [], exceptions: [] });
  const [loading, setLoading] = useState(true);
  const [viewMonth, setViewMonth] = useState(new Date());
  const userId = staff.user_id || staff.id;

  useEffect(() => {
    setLoading(true);
    api.get('/availability/staff/' + userId).then(r => {
      const d = r.data?.data || {};
      setData({ weekly: d.weekly || [], exceptions: d.exceptions || [] });
    }).catch(() => setData({ weekly: [], exceptions: [] }))
      .finally(() => setLoading(false));
  }, [userId]);

  const monthLabel = format(viewMonth, 'MMMM yyyy');

  return (
    <div className="space-y-4" data-testid="admin-availability-detail">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="admin-availability-back">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
        </Button>
        <div>
          <h2 className="text-lg font-bold tracking-tight flex items-center gap-2">
            <User className="w-4 h-4 text-muted-foreground" />
            {staff.full_name}
          </h2>
          <p className="text-xs text-muted-foreground">Read-only view of weekly schedule, exceptions and leave.</p>
        </div>
      </div>

      {loading ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Loading…</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Weekly Schedule</CardTitle></CardHeader>
            <CardContent>
              {data.weekly.length === 0 ? (
                <div className="text-xs text-muted-foreground py-4">No weekly schedule set.</div>
              ) : (
                <ul className="space-y-1.5">
                  {data.weekly.slice().sort((a, b) => a.day_of_week - b.day_of_week).map(w => {
                    const r = w.ranges?.[0] || {};
                    const dayName = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][w.day_of_week] || '?';
                    const lbl = rangeLabel(r);
                    const showBoth = (r.types || []).filter(t => t !== 'CUSTOM').length >= 1 && lbl !== `${r.start_time || ''}–${r.end_time || ''}`;
                    return (
                      <li key={w.day_of_week} className="flex items-center justify-between text-sm border rounded px-2 py-1.5 bg-emerald-50/40 border-emerald-200">
                        <span className="font-medium w-12">{dayName}</span>
                        <span className="text-xs text-emerald-800 flex items-center gap-2">
                          <span className="font-medium">{lbl}</span>
                          {showBoth && (
                            <span className="font-mono tabular-nums text-emerald-700/80 text-[10px]">{r.start_time}–{r.end_time}</span>
                          )}
                          {r.is_sleepover && (
                            <span className="inline-flex items-center text-[9px] px-1 py-0.5 rounded-full bg-violet-100 text-violet-800 border border-violet-300">Sleepover</span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Calendar Preview</CardTitle>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => setViewMonth(d => subMonths(d, 1))} data-testid="admin-avail-prev"><ChevronLeft className="w-3.5 h-3.5" /></Button>
                <span className="text-xs font-medium tabular-nums w-28 text-center">{monthLabel}</span>
                <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => setViewMonth(d => addMonths(d, 1))} data-testid="admin-avail-next"><ChevronRight className="w-3.5 h-3.5" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <MonthGrid date={viewMonth} weekly={data.weekly} exceptions={data.exceptions} />
              <div className="flex flex-wrap gap-2 pt-3 text-[10px] text-muted-foreground">
                <span className="inline-block w-3 h-3 rounded bg-emerald-50 border border-emerald-200" /> Available
                {Object.entries(EXC_META).map(([k, m]) => (
                  <span key={k} className="inline-flex items-center gap-1">
                    <span className={'inline-block w-3 h-3 rounded border ' + m.cls} />
                    {m.label}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader className="pb-2"><CardTitle className="text-base">Upcoming Exceptions &amp; Leave</CardTitle></CardHeader>
            <CardContent className="space-y-1.5">
              {data.exceptions.length === 0 ? (
                <div className="text-xs text-muted-foreground py-2">No exceptions set.</div>
              ) : (
                data.exceptions
                  .slice()
                  .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
                  .map(exc => {
                    const m = excMeta(exc.type);
                    return (
                      <div key={exc.date} className="flex items-center gap-3 text-sm border rounded-md p-2 bg-white">
                        <span className="font-mono tabular-nums w-24">{exc.date}</span>
                        <span className={'inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full border ' + m.cls}>{m.label}</span>
                        <span className="text-muted-foreground flex-1 truncate">{exc.notes}</span>
                      </div>
                    );
                  })
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground">
        Heads-up: availability is informational only — it does not currently enforce or block scheduling.
      </p>
    </div>
  );
}


export default function StaffAvailabilityViewerPage() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get('/staff?status=ACTIVE')
      .then(r => setStaff(r.data.items || r.data.data || []))
      .catch(() => setStaff([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return staff;
    return staff.filter(s => (s.full_name || '').toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q));
  }, [staff, search]);

  if (selected) {
    return (
      <div className="p-6">
        <StaffAvailabilityDetail staff={selected} onBack={() => setSelected(null)} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4 animate-fade-in" data-testid="admin-availability-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Staff Availability</h1>
        <p className="text-muted-foreground mt-1 text-sm">Read-only view of each staff member&apos;s weekly schedule and leave.</p>
      </div>

      <Card>
        <CardContent className="p-3">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Search staff by name or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="admin-availability-search"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8 text-sm text-muted-foreground">Loading staff…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">No staff match your search.</div>
          ) : (
            <ul className="divide-y">
              {filtered.map(s => (
                <li
                  key={s.id}
                  className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 cursor-pointer"
                  onClick={() => setSelected(s)}
                  data-testid={'admin-availability-staff-' + s.id}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold">
                      {(s.full_name || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium text-sm">{s.full_name}</div>
                      <div className="text-xs text-muted-foreground">{s.email}</div>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={'admin-availability-view-' + s.id}>
                    <Calendar className="w-3.5 h-3.5 mr-1" /> View
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
